import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams, clampPayloadToCurrentScope, filterItemsByCurrentScope } from './viewerAccess';
import { formatGpsCoordinates, toGeoJsonPoint } from '../services/gpsCoordinates';
import { publishCollectionChange } from '../utils/collectionSync';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid)
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (candidate.uuid || candidate.market_name || candidate.marketName) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({ basePath, normalize, buildPayload, defaultPerPage = 25, collectionKey }) {
  const syncKey = collectionKey ?? basePath.replace(/^\/v1\//, '').replace(/^\//, '').replace(/\/$/, '');

  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    const page = extractCollection(data, normalize);
    return {
      ...page,
      items: filterItemsByCurrentScope(page.items),
    };
  }

  return {
    collectionKey: syncKey,

    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      const item = extractItem(data, normalize);
      publishCollectionChange(syncKey, { action: 'create', uuid: item?.uuid ?? null });
      return item;
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'update', uuid });
      return item;
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      publishCollectionChange(syncKey, { action: 'remove', uuid });
      return data;
    },

    // Validation workflow — backend routes/api.php validationTransitions('markets').
    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      publishCollectionChange(syncKey, { action: 'submit', uuid });
      return extractItem(data, normalize, uuid);
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      publishCollectionChange(syncKey, { action: 'validate', uuid });
      return extractItem(data, normalize, uuid);
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      publishCollectionChange(syncKey, { action: 'approve', uuid });
      return extractItem(data, normalize, uuid);
    },

    async reject(uuid, notes = '') {
      if (!notes || !notes.trim()) throw new Error('Rejection reason is required.');
      const { data } = await client.post(`${basePath}/${uuid}/reject`, { notes: notes.trim() });
      publishCollectionChange(syncKey, { action: 'reject', uuid });
      return extractItem(data, normalize, uuid);
    },
  };
}

function normalizeMarket(row = {}) {
  const marketObj = row.market && typeof row.market === 'object' ? row.market : {};

  const marketName =
    row.market_name ??
    row.marketName ??
    marketObj.market_name ??
    marketObj.marketName ??
    marketObj.name ??
    '';

  const marketType =
    row.market_type ??
    row.marketType ??
    marketObj.market_type ??
    marketObj.marketType ??
    '';

  const marketSchedule =
    row.market_schedule ??
    row.marketSchedule ??
    marketObj.market_schedule ??
    marketObj.marketSchedule ??
    '';

  return {
    uuid: row.uuid ?? row.id ?? marketObj.uuid ?? null,
    id: row.uuid ?? row.id ?? marketObj.uuid ?? null,
    market_name: marketName,
    marketName,
    market_type: marketType,
    marketType,
    market_schedule: marketSchedule,
    marketSchedule,
    status: row.status ?? marketObj.status ?? '',
    // Record workflow status (draft → submitted → validated → approved),
    // separate from the market's operational `status` above.
    data_status: String(row.data_status ?? row.dataStatus ?? marketObj.data_status ?? 'draft').toLowerCase(),
    rejection_notes: row.rejection_notes ?? row.rejectionNotes ?? null,
    state: row.state ?? marketObj.state ?? '',
    lga: row.lga ?? marketObj.lga ?? '',
    contact: row.contact ?? marketObj.contact ?? '',
    location: row.location ?? marketObj.location ?? '',
    established: row.established ?? marketObj.established ?? '',
    description: row.description ?? marketObj.description ?? '',
    gps_coordinates: formatGpsCoordinates(row.gps_coordinates ?? row.gpsCoordinates ?? marketObj.gps_coordinates ?? null),
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildMarketPayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  return {
    state: trimOrNull(scopedPayload.state),
    lga: trimOrNull(scopedPayload.lga),
    market_name: trimOrNull(scopedPayload.market_name ?? scopedPayload.marketName),
    market_type: trimOrNull(scopedPayload.market_type ?? scopedPayload.marketType),
    status: trimOrNull(scopedPayload.status),
    market_schedule: trimOrNull(scopedPayload.market_schedule ?? scopedPayload.marketSchedule),
    contact: trimOrNull(scopedPayload.contact),
    location: trimOrNull(scopedPayload.location),
    established: trimOrNull(scopedPayload.established),
    description: trimOrNull(scopedPayload.description),
    gps_coordinates: toGeoJsonPoint(scopedPayload.gps_coordinates ?? scopedPayload.gpsCoordinates),
  };
}

const marketsBaseApi = makeCollectionApi({
  basePath: '/v1/markets',
  normalize: normalizeMarket,
  buildPayload: buildMarketPayload,
  defaultPerPage: 25,
  collectionKey: 'markets',
});

export const marketsApi = withOfflineCollectionSupport(marketsBaseApi, {
  basePath: '/v1/markets',
  collectionKey: 'markets',
  normalize: normalizeMarket,
  buildPayload: buildMarketPayload,
});

export { normalizeMarket, buildMarketPayload };
